import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon, MapPinIcon } from 'lucide-react';
import type { NetworkCampus } from '../../types/network';
import { campusPhotos, landingImages } from '../../data/landingVisual';
interface CampusCarouselProps {
  campuses: NetworkCampus[];
}

/**
 * Campus discovery.
 *
 * Status comes from the campus register, so a campus can only read as active
 * if its record says it is. Nothing here claims a partnership, and no campus
 * statistic is published.
 */
export function CampusCarousel({
  campuses
}: CampusCarouselProps) {
  const railRef = useRef<HTMLUListElement>(null);
  const scrollBy = (direction: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) return;
    rail.scrollBy({
      left: direction * (rail.clientWidth * 0.7),
      behavior: 'smooth'
    });
  };
  return <section aria-labelledby="campus-heading" className="bg-canvas py-16 lg:py-20" data-id="element-2211" data-mp="1nqls3ujl75fq">
      <div className="mx-auto max-w-7xl px-4 sm:px-6" data-id="element-2212" data-mp="1nsot19ma2iz8">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-2213" data-mp="14djue4zv30bf">
          <div data-id="element-2214" data-mp="zdnnnxzs4us6">
            <h2 id="campus-heading" className="text-h2 font-bold tracking-tight text-ink" data-id="element-2215" data-mp="ze6zqy2r0oya">
              Find your campus
            </h2>
            <p className="mt-2 max-w-[56ch] text-body text-ink-muted" data-id="element-2216" data-mp="1d63nkpxdwcov">
              Choose your campus and discover the services, communities and opportunities available
              to its students. Campuses open one at a time.
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-2217" data-mp="1i2u1b6lpuw74">
            <Link to="/welcome/campuses" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-2218" data-mp="1i69h6bn7s99l">
              View all campuses
              <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-2219" data-mp="1ovkq92dz4mt" />
            </Link>
            <div className="hidden gap-2 lg:flex" data-id="element-2220" data-mp="1wtlguxwjcwec">
              {([-1, 1] as const).map(direction => <button key={direction} type="button" onClick={() => scrollBy(direction)} aria-label={direction === -1 ? 'Previous campuses' : 'Next campuses'} className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-surface text-ink-muted transition-colors duration-150 ease-out hover:border-line-strong hover:text-ink" data-id="element-2221" data-mp="21sxzf6gdjhb4">
                  {direction === -1 ? <ChevronLeftIcon aria-hidden className="h-5 w-5" data-id="element-2222" data-mp="2pisd1q8ip9wf" /> : <ChevronRightIcon aria-hidden className="h-5 w-5" data-id="element-2223" data-mp="2ufisqcgeu3hb" />}
                </button>)}
            </div>
          </div>
        </div>

        <ul ref={railRef} className="cz-tabstrip mt-8 flex snap-x snap-mandatory gap-5" data-id="element-2224" data-mp="33gqrevmu96bx">
          {campuses.map(campus => {
          const label = campus.institution;
          return <li key={campus.id} className="w-[76vw] max-w-xs shrink-0 snap-start sm:w-[44vw] lg:w-[calc((100%-3.75rem)/4)]" data-id="element-2225" data-mp="nrigopjktmzl">
                <Link to="/welcome/campuses" className="group flex h-full flex-col overflow-hidden rounded-card border border-line bg-surface transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-1 hover:border-brand-200 hover:shadow-[0_16px_40px_rgba(15,23,42,0.1)]" data-id="element-2226" data-mp="dunlogmii4q2">
                  <span className="block aspect-[16/10] overflow-hidden" data-id="element-2227" data-mp="11pzyk6msw59l">
                    <img src={campusPhotos[campus.id] ?? landingImages.heroCampus} alt={`The ${label} campus`} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]" data-id="element-2228" data-mp="wsglha1u9yj8" />
                  </span>
                  <span className="flex flex-1 flex-col px-4 py-4" data-id="element-2229" data-mp="1e1hjo8z1ufyi">
                    <span className="block text-body font-semibold text-ink" data-id="element-2230" data-mp="101g6pv5ifi2o">{label}</span>
                    <span className="mt-1 flex items-center gap-1.5 text-body-sm text-ink-muted" data-id="element-2231" data-mp="103sxmxt8x2rv">
                      <MapPinIcon aria-hidden className="h-4 w-4 shrink-0" data-id="element-2232" data-mp="152dz10k8hshw" />
                      {campus.city}, {campus.state}
                    </span>
                    <span className="mt-auto pt-4" data-id="element-2233" data-mp="1xo6wqvqoagxt">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-caption font-semibold ${campus.active ? 'bg-brand-50 text-brand-800' : 'bg-warn-50 text-warn-800'}`} data-id="element-2234" data-mp="1j0c0x6hdbwgi">
                        {campus.active ? 'Active campus' : campus.recruitmentStatus}
                        <ArrowRightIcon aria-hidden className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-0.5" data-id="element-2235" data-mp="4zpfkzxn3fag" />
                      </span>
                    </span>
                  </span>
                </Link>
              </li>;
        })}
        </ul>
      </div>
    </section>;
}